import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import { toast } from "react-toastify";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const convertToBase64 = (url) => {
  return new Promise((resolve) => {
    if (!url) return resolve("/assets/images/idcard/staff.png");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve("/assets/images/idcard/staff.png");
    img.src = url;
  });
};

export const capitalizeWords = (str = "") =>
  str
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\s+/g, " ");

const StaffIdCard = async (data) => {
  if (!data) {
    toast.error("Missing staff details", { position: "top-right" });
    return;
  }

  const staffList = Array.isArray(data) ? data : [data];
  const targetWidth = 54;
  const targetHeight = 86;
  const pdf = new jsPDF("p", "mm", [targetWidth, targetHeight]);

  for (let i = 0; i < staffList.length; i++) {
    const staff = staffList[i];
    const basic = staff.basicDetails || {};
    const category = staff.categoryDetails || {};
    const transport = staff.transportFacility || {};

    try {
      const [staffImage, qrDataURL] = await Promise.all([
        convertToBase64(basic.staffImage),
        QRCode.toDataURL(staff.staffId || "STAFF"),
      ]);

      const container = document.createElement("div");
      Object.assign(container.style, {
        position: "fixed",
        top: "-10000px",
        left: "-10000px",
        width: "210mm",
        height: "335mm",
        fontFamily: "Eczar, serif",
      });

      container.innerHTML = `
      <div class="container-idcard">
        <div class="pb-2 px-5 mx-3 mt-4 position-relative">
          <div class="d-flex align-items-start justify-content-between">
            <img src="/assets/images/idcard/patanjali-logo.png" width="260" />
            <img src="/assets/images/idcard/logo.png" width="160" height="160" />
          </div>
          <div class="mt-n6">
            <h1 class="id-text-primary display-1 fw-bold mb-2 lh-1 eczar-font">आचार्यकुलम्</h1>
            <p class="id-text-primary id-text-medium fw-bold lh-sm m-0"><i class="bi bi-geo-alt-fill me-2"></i> Tata Road, Namkum, Ranchi - 834010</p>
            <p class="id-text-primary id-text-medium fw-bold lh-sm m-0"><i class="bi bi-telephone-fill me-2"></i> (+91) 6287842460 / 64</p>
          </div>
        </div>

        <div class="mx-3">
          <div class="px-5 py-4 my-2">
            <div class="row align-items-center g-0">
              <div class="col-5 text-center">
                <div style="
                  width: 260px;
                  height: 260px;
                  background-image: url('${staffImage}');
                  background-size: cover;
                  background-position: center;
                  border-radius: 100%;
                  border: 2px solid orange;
                "></div>
              </div>
              <div class="col-7 ps-3">
                <p class="id-text-normal fw-medium lh-sm mb-1 text-black">Employee ID: <b>${
                  staff.staffId
                }</b></p>
                <p class="id-text-normal fw-medium lh-sm mb-1 text-black">DOB: <b>${new Date(
                  basic.dob
                ).toLocaleDateString("en-GB")}</b></p>
                <p class="id-text-normal fw-medium lh-sm mb-1 text-black">Mobile: <b>${
                  basic.mobNo
                }</b></p>
                <p class="id-text-normal fw-medium lh-sm mb-1 text-black">Blood Group: <b>${
                  basic.bloodGroup
                }</b></p>
              </div>
            </div>
          </div>

          <div class="px-5">
            <h2 class="text-dark display-5 text-uppercase fw-bold mb-1">${
              basic.fullName
            }</h2>
            <p class="id-text-normal fw-medium lh-sm mb-1">${
              category.designation
            }</p>
          </div>

          <div class="px-5">
            <p class="id-text-normal fw-medium lh-sm mb-1 text-black">Department: <b>${
              category.department
            }</b></p>
            <p class="id-text-normal fw-medium lh-sm mb-1 text-black mt-6">Address: <b>${capitalizeWords(
              basic.permanentAddress
            )}</b></p>
            <p class="text-dark h2 m-0 fw-medium lh-1 mt-4 eczar-font">Bus Stoppage :</p>
            <p class="text-dark id-text-large m-0 fw-semibold mb-1 eczar-font"><i class="bi bi-bus-front-fill id-icon-primary"></i> ${capitalizeWords(
              transport?.busStop || "NONE"
            )}</p>
            <div class="d-flex">
              <p class="text-dark h2 m-0 fw-semibold me-3 eczar-font"> ${
                transport?.busNumber ? "Bus No" : "Self"
              } :</p> 
              <div class="id-circle">
                <div class="id-content">
                  <b class="text-dark id-text-large m-0 fw-bold eczar-font">
                    ${
                      transport.busNumber ||
                      '<i class="bi bi-person-walking"></i>'
                    }
                  </b>
                </div>
              </div>
            </div>
            
            <div class="float-end mt-7">
              <div class="me-3 text-center">
                <img
                  src="/assets/images/idcard/director.png"
                  width="190"
                  height="70"
                  class="object-fit-contain"
                />
                <p class="text-dark h1 m-0 fw-bold lh-sm eczar-font">Issuing Authority</p>
              </div>
            </div>
          </div>
        </div>
      </div>`;

      document.body.appendChild(container);
      await sleep(100);

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });
      container.remove();

      const imgData = canvas.toDataURL("image/png");
      const ratio = Math.min(
        targetWidth / canvas.width,
        targetHeight / canvas.height
      );
      const imgWidth = canvas.width * ratio;
      const imgHeight = canvas.height * ratio;
      const x = (targetWidth - imgWidth) / 2;
      const y = (targetHeight - imgHeight) / 2;

      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
      await sleep(200);
    } catch (err) {
      console.error("Error rendering staff:", staff, err);
      toast.error(
        `Error rendering ${staff?.basicDetails?.fullName || "Unknown"}`
      );
    }
  }

  try {
    pdf.save("Staff_ID_Cards.pdf");
  } catch (e) {
    toast.error("Failed to save PDF");
  }
};

export default StaffIdCard;
